import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
    AnalyticsConsent,
    applyAnalyticsConsent,
    getStoredAnalyticsConsent,
    initAnalyticsFromStoredConsent,
} from '@/services/analytics'
import CookieConsentDialog from '@/components/legal/CookieConsentDialog'

interface CookieConsentContextValue {
    consent: AnalyticsConsent | null
    hasResponded: boolean
    setConsent: (consent: AnalyticsConsent) => void
}

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined)

export const useCookieConsent = (): CookieConsentContextValue => {
    const context = useContext(CookieConsentContext)
    if (!context) {
        throw new Error('useCookieConsent must be used within CookieConsentProvider')
    }
    return context
}

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [consent, setConsentState] = useState<AnalyticsConsent | null>(() => getStoredAnalyticsConsent())

    useEffect(() => {
        initAnalyticsFromStoredConsent()
    }, [])

    const setConsent = useCallback((value: AnalyticsConsent) => {
        applyAnalyticsConsent(value)
        setConsentState(value)
    }, [])

    const value = useMemo(() => ({
        consent,
        hasResponded: consent !== null,
        setConsent,
    }), [consent, setConsent])

    return (
        <CookieConsentContext.Provider value={value}>
            {children}
            <CookieConsentDialog
                open={!value.hasResponded}
                onAccept={() => setConsent('granted')}
                onReject={() => setConsent('denied')}
            />
        </CookieConsentContext.Provider>
    )
}
